// RPTCAT0: transport and presentation adapters only. The server owns scope, dates and all report arithmetic.
export interface CannedOriginDto {
  id: string;
  version: number;
  hash: string;
}
export interface CannedLocationDto {
  id: string;
  name: string;
  franchisee?: string;
  /** CRB5-F3: a suspended franchisee's location. */
  suspended?: boolean;
}
export interface CannedCatalogEntry {
  id: string;
  title: string;
  group: string;
  availability: string;
  unavailableReason: string | null;
  /** RPTLAYOUT1-F1-S4: a plain sentence for a report the platform has no record to build from. It shows as a disabled card with a circled ? holding this sentence. */
  plainReason?: string;
  template: { version: number; hash: string } | null;
  datePresets?: string[];
  selectedCategoryTemplate?: { version: number; hash: string };
  categoryOptionsPath?: string;
  compareKinds?: string[];
  chartBases?: string[];
  chartMetrics?: string[];
  defaultChartMetric?: string;
  directions?: Array<"incoming" | "outgoing">;
  maxSelectedLocations?: number;
  maxRangeDays?: number;
  requiresCountSession?: boolean;
  requiresTwoCountSessions?: boolean;
  canRun: boolean;
  canExport: boolean;
  canSaveDerivative: boolean;
  canSchedule: boolean;
}
export interface CannedCatalogDto {
  reports: CannedCatalogEntry[];
  scope: {
    kind: "fixed_location" | "network";
    locations: CannedLocationDto[];
    businessDate: string;
    timezone: string;
  };
}
export interface CannedResultDto extends Record<string, unknown> {
  template: CannedOriginDto;
  executionSpecHash: string;
  generatedAt: string;
  scope: { kind: string; locations: CannedLocationDto[]; locationCount: number };
  rows: Record<string, unknown>[];
  display: { truncated: boolean; [key: string]: unknown };
}
// Catalog IDs and immutable origin IDs are deliberately distinct for several renditions.
export const CANNED_FAMILIES: Readonly<Record<string, { origin: string; route: string }>> = {
  "network-current-open-purchase-orders": {
    origin: "network-current-open-purchase-orders",
    route: "network-current-open-purchase-orders",
  },
  "current-open-purchase-orders": {
    origin: "current-open-purchase-orders",
    route: "current-open-purchase-orders",
  },
  "recorded-transfer-quantities": {
    origin: "recorded-transfer-quantities",
    route: "recorded-transfer-quantities",
  },
  "sales-summary": { origin: "sales-summary", route: "sales-summary" },
  "daily-sales": { origin: "daily-sales", route: "daily-sales" },
  "sales-by-weekday": { origin: "sales-by-weekday", route: "sales-by-weekday" },
  "sales-journal": { origin: "sales-journal", route: "sales-journal" },
  "product-mix": { origin: "product-mix", route: "product-mix" },
  "category-share": { origin: "category-share", route: "category-share" },
  "weekly-item-movement": { origin: "weekly-item-movement", route: "weekly-item-movement" },
  "location-comparison": { origin: "location-comparison", route: "location-comparison" },
  "royalty-basis": { origin: "royalty-basis", route: "royalty-basis" },
  "current-inventory-value": {
    origin: "current-inventory-value",
    route: "current-inventory-value",
  },
  "closed-count-session-summary": {
    origin: "closed-count-session-summary",
    route: "closed-count-session-summary",
  },
  "closed-count-item-details": {
    origin: "recorded-closed-count-item-details",
    route: "closed-count-item-details",
  },
  "closed-count-comparison": {
    origin: "recorded-closed-count-comparison",
    route: "closed-count-comparison",
  },
  "received-delivery-cost": { origin: "received-delivery-cost", route: "received-delivery-cost" },
  "received-spend-by-vendor": {
    origin: "received-spend-by-vendor",
    route: "received-spend-by-vendor",
  },
  "prepared-voids": { origin: "prepared-voids-by-reason-actor", route: "prepared-voids" },
  "discount-applications": {
    origin: "discount-application-activity",
    route: "discount-applications",
  },
  "recorded-refunds": { origin: "recorded-refunds", route: "recorded-refunds" },
  "paid-in-out": { origin: "paid-in-out", route: "paid-in-out" },
  walkouts: { origin: "walkout-closeout-metrics", route: "walkouts" },
  "price-overrides": { origin: "ticket-price-change-commands", route: "price-overrides" },
  "payments-by-method": { origin: "payments-by-method", route: "payments-by-method" },
  "loss-prevention-trends": {
    origin: "daily-loss-prevention-trends",
    route: "loss-prevention-trends",
  },
  "tips-by-employee-day": { origin: "tips-by-employee-day", route: "tips-by-employee-day" },
  "employee-activity": { origin: "employee-preparation-void-activity", route: "employee-activity" },
  "guest-ticket-activity": { origin: "guest-ticket-activity", route: "guest-ticket-activity" },
  "preparation-stock-movements": {
    origin: "recorded-preparation-stock-movements",
    route: "preparation-stock-movements",
  },
  "waste-reason-activity": {
    origin: "recorded-waste-reason-activity",
    route: "waste-reason-activity",
  },
  // RPTLAYOUT1-F1-S4: the Software Owner's platform reports (src/domain/reportBuilder/platformCannedFigures.ts holds the same ids).
  ...Object.fromEntries(
    [
      "active-customers-by-plan",
      "monthly-recurring-revenue",
      "new-customers",
      "trials-ending",
      "failed-payments",
      "churned-customers",
      "upcoming-renewals",
      "ai-usage-by-customer",
      "ai-usage-by-person",
      "ai-spend-against-caps",
      "customers-by-onboarding-stage",
      "time-to-go-live",
      "royalties-collected-vs-due",
    ].map((route) => [`platform-${route}`, { origin: `platform-${route}`, route }]),
  ),
};
/** A snapshot report (no date presets, as the platform reports of the current state): it takes no dates at all. */
export function hasNoDates(entry: CannedCatalogEntry): boolean {
  return entry.datePresets !== undefined && entry.datePresets.length === 0;
}
export function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
export function originDto(value: unknown): value is CannedOriginDto {
  return (
    record(value) &&
    typeof value["id"] === "string" &&
    Number.isSafeInteger(value["version"]) &&
    Number(value["version"]) > 0 &&
    typeof value["hash"] === "string" &&
    /^[a-f0-9]{64}$/.test(value["hash"])
  );
}
export function cannedFamily(origin: unknown): string | undefined {
  return originDto(origin)
    ? Object.keys(CANNED_FAMILIES).find((id) => CANNED_FAMILIES[id]?.origin === origin.id)
    : undefined;
}
export function isCannedResult(value: unknown): value is CannedResultDto {
  return (
    record(value) &&
    originDto(value["template"]) &&
    typeof value["executionSpecHash"] === "string" &&
    record(value["scope"]) &&
    Array.isArray(value["scope"]["locations"]) &&
    Array.isArray(value["rows"]) &&
    value["rows"].every(record) &&
    record(value["display"]) &&
    typeof value["display"]["truncated"] === "boolean"
  );
}
export function isCannedCatalog(value: unknown): value is CannedCatalogDto {
  return (
    record(value) &&
    Array.isArray(value["reports"]) &&
    value["reports"].every(
      (r) =>
        record(r) &&
        typeof r["id"] === "string" &&
        typeof r["title"] === "string" &&
        typeof r["canRun"] === "boolean",
    ) &&
    record(value["scope"]) &&
    Array.isArray(value["scope"]["locations"]) &&
    value["scope"]["locations"].every(
      (r) => record(r) && typeof r["id"] === "string" && typeof r["name"] === "string",
    ) &&
    typeof value["scope"]["businessDate"] === "string"
  );
}
export interface CannedFilters {
  preset: string;
  from: string;
  to: string;
  compare: string;
  chartBasis: string;
  chartMetric: string;
  direction?: "incoming" | "outgoing";
  category?: string | null;
  allLocations: boolean;
  locationIds: string[];
  countSessionId: string;
  baselineCountSessionId: string;
  comparisonCountSessionId: string;
}
export function initialCannedFilters(
  entry: CannedCatalogEntry,
  catalog: CannedCatalogDto,
): CannedFilters {
  return {
    preset: entry.datePresets?.[0] ?? "",
    from: "",
    to: "",
    compare: "",
    chartBasis: entry.chartBases?.[0] ?? "",
    chartMetric: entry.defaultChartMetric ?? "",
    ...(entry.selectedCategoryTemplate ? { category: null } : {}),
    ...(entry.id === "recorded-transfer-quantities" ? { direction: entry.directions?.[0] } : {}),
    allLocations: entry.maxSelectedLocations !== 1,
    locationIds:
      entry.maxSelectedLocations === 1 ? catalog.scope.locations.slice(0, 1).map((i) => i.id) : [],
    countSessionId: "",
    baselineCountSessionId: "",
    comparisonCountSessionId: "",
  };
}
export function cannedRunBody(
  entry: CannedCatalogEntry,
  filters: CannedFilters,
  catalog: CannedCatalogDto,
): Record<string, unknown> {
  if (entry.template === null || !CANNED_FAMILIES[entry.id])
    throw new Error("This report is unavailable.");
  if (!filters.allLocations && filters.locationIds.length === 0)
    throw new Error("Choose a location.");
  if (
    entry.maxSelectedLocations !== undefined &&
    (filters.allLocations ? catalog.scope.locations.length : filters.locationIds.length) >
      entry.maxSelectedLocations
  )
    throw new Error(`Choose up to ${entry.maxSelectedLocations} location.`);
  const body: Record<string, unknown> = {
    templateVersion: entry.template.version,
    templateHash: entry.template.hash,
    ...(catalog.scope.kind === "fixed_location"
      ? { locationId: catalog.scope.locations[0]?.id }
      : {
          locations: filters.allLocations
            ? { kind: "all_permitted" }
            : { kind: "list", ids: filters.locationIds },
        }),
  };
  if (entry.id === "network-current-open-purchase-orders" && catalog.scope.kind !== "network")
    throw new Error("Choose this report in the franchisor console.");
  if (
    entry.id === "current-open-purchase-orders" &&
    (catalog.scope.kind !== "fixed_location" || catalog.scope.locations.length !== 1)
  )
    throw new Error("Choose one location in the location console.");
  if (entry.id === "recorded-transfer-quantities") {
    if (catalog.scope.kind !== "fixed_location" || catalog.scope.locations.length !== 1)
      throw new Error("Choose one location in the location console.");
    if (
      (filters.direction !== "incoming" && filters.direction !== "outgoing") ||
      !entry.directions?.includes(filters.direction)
    )
      throw new Error("Choose Incoming or Outgoing.");
    body["direction"] = filters.direction;
  }
  if (entry.id !== "current-inventory-value" && !hasNoDates(entry)) {
    if (filters.preset === "custom" && (!filters.from || !filters.to || filters.from > filters.to))
      throw new Error("Choose a valid date range.");
    body["dateRange"] =
      filters.preset === "custom"
        ? { kind: "absolute", from: filters.from, to: filters.to }
        : { kind: "relative", preset: filters.preset };
  }
  if (
    [
      "sales-summary",
      "daily-sales",
      "sales-by-weekday",
      "sales-journal",
      "location-comparison",
      "royalty-basis",
    ].includes(entry.id)
  ) {
    body["compare"] = filters.compare ? { kind: filters.compare } : null;
    body["chartBasis"] = filters.chartBasis;
  }
  if (
    ["product-mix", "category-share", "weekly-item-movement", "received-delivery-cost"].includes(
      entry.id,
    )
  ) {
    body["compare"] = filters.compare ? { kind: filters.compare } : null;
    if (entry.id !== "received-delivery-cost") {
      body["categories"] = "all";
      if (filters.category !== undefined && filters.category !== null) {
        if (!entry.selectedCategoryTemplate || !entry.categoryOptionsPath)
          throw new Error("Category selection is unavailable.");
        body["categories"] = { kind: "named", value: filters.category };
        body["templateVersion"] = entry.selectedCategoryTemplate.version;
        body["templateHash"] = entry.selectedCategoryTemplate.hash;
      }
    }
  }
  if (
    [
      "current-inventory-value",
      "walkouts",
      "closed-count-session-summary",
      "received-delivery-cost",
      "prepared-voids",
      "discount-applications",
      "recorded-refunds",
      "paid-in-out",
      "price-overrides",
      "payments-by-method",
    ].includes(entry.id)
  )
    body["chartBasis"] = filters.chartBasis;
  if (entry.id === "received-spend-by-vendor")
    body["compare"] = filters.compare ? { kind: filters.compare } : null;
  if (entry.id === "loss-prevention-trends") body["chartMetric"] = filters.chartMetric;
  if (entry.requiresCountSession) {
    if (!filters.countSessionId) throw new Error("Choose a closed count session.");
    body["countSessionId"] = filters.countSessionId;
  }
  if (entry.requiresTwoCountSessions) {
    if (
      !filters.baselineCountSessionId ||
      !filters.comparisonCountSessionId ||
      filters.baselineCountSessionId === filters.comparisonCountSessionId
    )
      throw new Error("Choose two different closed count sessions.");
    body["baselineCountSessionId"] = filters.baselineCountSessionId;
    body["comparisonCountSessionId"] = filters.comparisonCountSessionId;
  }
  return body;
}
export function cannedLinkOnly(origin: CannedOriginDto): boolean {
  return [
    "sales-journal",
    "prepared-voids-by-reason-actor",
    "discount-application-activity",
    "tips-by-employee-day",
    "employee-preparation-void-activity",
  ].includes(origin.id);
}
export function reportWords(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/^./, (s) => s.toUpperCase());
}
export const hiddenKeys = new Set([
  "key",
  "locationId",
  "executionSpecHash",
  "catalogVersion",
  "technicalTotals",
]);
export function flat(
  value: Record<string, unknown>,
  prefix = "",
  output: Record<string, unknown> = {},
): Record<string, unknown> {
  for (const [key, cell] of Object.entries(value)) {
    if (hiddenKeys.has(key)) continue;
    const name = prefix ? `${prefix}.${key}` : key;
    if (record(cell)) flat(cell, name, output);
    else if (!Array.isArray(cell)) output[name] = cell;
  }
  return output;
}
export function label(key: string): string {
  return key.split(".").map(reportWords).join(" · ");
}
export function cellText(value: unknown, key: string): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "-";
    if (/(?:Bp|_bp)(?:\.|$)/.test(key) || key.startsWith("changeBp."))
      return `${(value / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
    if (/cents/i.test(key))
      return (value / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
    return value.toLocaleString("en-US", { maximumFractionDigits: 4 });
  }
  return String(value);
}
